import { defineStore } from 'pinia';
import { AgendamentoDTO, AgendamentoService, TipoAgendamento, UpdateAgendamento } from '@/services/agendamentos/agendamentos.service';
import { toast } from 'vue3-toastify';
import { useContatoStore } from './contatoStore';
import { useAuthStoreUser } from './authUser';
import { useChatStore } from './chatStore';
import { StorageService } from '../admin/storage';
import { IdAgendamento, IdContato, IdGrupo } from '@/services/common-types';

export const useScheduleStore = defineStore('schedule', {
    state: () => ({
        scheduleService: new AgendamentoService(),
        storageService: new StorageService(),
        isSaving: false as boolean,
        schedules: [] as AgendamentoDTO[],
        isManagerOpen: false as boolean,
        isFormOpen: false as boolean,
        activeContactFilter: null as IdContato | null,
        activeContactName: null as string | null,
        activeGroupFilter: null as IdGrupo | null,
        activeGroupName: null as string | null,
        scheduleToEdit: null as AgendamentoDTO | null,
        preSelectedDate: null as Date | null,
        isFetching: false as boolean,
    }),

    getters: {
        filteredSchedules: (state) => {
            if (state.activeGroupFilter) return state.schedules.filter(s => s.idGrupo === state.activeGroupFilter);
            if (state.activeContactFilter) return state.schedules.filter(s => s.idContato === state.activeContactFilter);
            return state.schedules;
        },

        hasPendingSchedule: (state) => (contactId: string) => {
            return state.schedules.some(s =>
                s.idContato === contactId && new Date(s.datetimeEnvio) > new Date()
            );
        },

        hasPendingScheduleGrupo: (state) => (idGrupo: string) => {
            return state.schedules.some(s =>
                s.idGrupo === idGrupo && new Date(s.datetimeEnvio) > new Date()
            );
        },
    },

    actions: {
        async fetchSchedules() {
            const authStore = useAuthStoreUser();
            if (!authStore.currentConta?.id) return;
            
            if (this.isFetching) return;
            
            try {
                this.isFetching = true;
                this.schedules = await this.scheduleService.getAllByConta(authStore.currentConta.id);
            } catch (e) {
                console.error("Erro ao atualizar agendamentos:", e);
            } finally {
                this.isFetching = false;
            }
        },
        // Helper seguro para buscar nome
        getContactName(idContato?: IdContato | null) {
            if (!idContato) return 'Desconhecido';
            const contatoStore = useContatoStore();
            const c = contatoStore.contatos.find(ct => ct.id === idContato);
            if (c) return c.nome;
            void contatoStore.findByIdCached(idContato);
            return '...';
        },

        // Resolve nome do grupo a partir da lista de chats já carregada.
        getGroupName(idGrupo?: IdGrupo | null) {
            if (!idGrupo) return 'Grupo';
            const chatStore = useChatStore();
            const g = chatStore.chatList.find(c => c.isGroup && c.id === idGrupo);
            return g?.name || 'Grupo';
        },

        // Permite abrir já filtrado (contato OU grupo) ou vazio.
        async openManager(targetId: IdContato | IdGrupo | null = null, targetName: string | null = null, isGroup = false) {
            if (isGroup) {
                this.activeGroupFilter = targetId as IdGrupo | null;
                this.activeGroupName = targetName;
                this.activeContactFilter = null;
                this.activeContactName = null;
            } else {
                this.activeContactFilter = targetId as IdContato | null;
                this.activeContactName = targetName;
                this.activeGroupFilter = null;
                this.activeGroupName = null;
            }
            this.isManagerOpen = true;
            
            await this.fetchSchedules();
            const authStore = useAuthStoreUser();
            if (!authStore.currentConta?.id) {
                this.schedules = [];
                return;
            }
            this.schedules = await this.scheduleService.getAllByConta(authStore.currentConta.id)
        },

        // Ação para alterar o filtro de dentro do modal
        setContactFilter(contactId: IdContato, contactName: string) {
            this.activeContactFilter = contactId;
            this.activeContactName = contactName;
        },

        clearContactFilter() {
            this.activeContactFilter = null;
            this.activeContactName = null;
            this.activeGroupFilter = null;
            this.activeGroupName = null;
        },

        openCreateForm(contactId?: IdContato, contactName?: string, initialDate?: Date, groupId?: IdGrupo, groupName?: string) {
            this.scheduleToEdit = null;
            this.preSelectedDate = initialDate || null; // Salva a data

            const targetGroupId = groupId || this.activeGroupFilter;
            if (targetGroupId) {
                this.activeGroupFilter = targetGroupId;
                this.activeGroupName = groupName || this.activeGroupName || null;
                this.activeContactFilter = null;
                this.activeContactName = null;
            } else {
                const targetId = contactId || this.activeContactFilter;
                const targetName = contactName || this.activeContactName;
                if (targetId) {
                    this.activeContactFilter = targetId;
                    this.activeContactName = targetName || null;
                }
            }
            this.isFormOpen = true;
        },

        openEditForm(schedule: AgendamentoDTO) {
            this.scheduleToEdit = schedule;
            this.isFormOpen = true;
        },

        async saveSchedule(schedule: AgendamentoDTO, file?: File) {
            if (this.isSaving) return;
            try {
                this.isSaving = true;
                schedule.datetimeEnvio = new Date(schedule.datetimeEnvio).toISOString();

                if (schedule.tipo == TipoAgendamento.Anexo && file){
                    if (!schedule.idInbox) throw new Error('[scheduleStore] [saveSchedule] `schedule.idInbox` was `undefined`.')
                    const uploadResult = await this.storageService.uploadAgendamentoFile({file: file, idInbox: schedule.idInbox})
                    schedule.url = uploadResult.path;
                }

                if (schedule.id) {
                    const updated = await this.scheduleService.update(schedule as UpdateAgendamento);
                    const index = this.schedules.findIndex(s => s.id === schedule.id);
                    if (index !== -1) this.schedules[index] = updated;
                    toast.success('Agendamento atualizado!');
                } else {
                    schedule.idUsuario = useAuthStoreUser()?.user?.id;
                    const created = await this.scheduleService.create(schedule);
                    this.schedules.push(created);
                    toast.success('Agendamento criado!');
                }
                this.isFormOpen = false;
            } catch (e) {
                console.error(e);
                toast.error((e as Error).message || 'Não foi possível salvar o agendamento.')
            } finally {
                this.isSaving = false;
            }
        },

        async deleteSchedule(scheduleId: IdAgendamento) {
            if (this.isSaving) return;
            try {
                this.isSaving = true;
                await this.scheduleService.delete(scheduleId);
                this.schedules = this.schedules.filter(s => s.id !== scheduleId);
                toast.success('Agendamento removido.');
            } catch (e) {
                console.error(e);
                toast.error((e as Error).message || `Não foi possível remover o agendamento.`);
            } finally {
                this.isSaving = false;
            }
        }
    }
});